import React, {
  memo,
  Suspense,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AHIcon,
  AHQueryHandlerLoading,
  Box,
  Circle,
  TouchableBox,
  useBoxStyle,
  useTheme,
} from "arkh";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  View,
} from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useSubscription,
} from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../components/mobile/QueryNetworkBoundrary";
import { TodoEditNewMainComponent_todo$key } from "./__generated__/TodoEditNewMainComponent_todo.graphql";
import { DropFileZone, droppedFileToImage } from "../../Files/DropFileZone";

import { TodoEditScreenProps } from "../paramList";

import { TodoEditNewMainQuery } from "./__generated__/TodoEditNewMainQuery.graphql";
import { TodoEditNewMainFooter } from "./TodoEditNewMainFooter/TodoEditNewMainFooter";
import { useNavigationHeaderOptions } from "../../../common/navigationHeaderOptions";
import { useTodoEditMoreMenuItems } from "./useTodoEditMoreMenuItems";
import { useTodoSaveStatus } from "./useTodoSaveStatus";

import { useSafeAreaInsets } from "react-native-safe-area-context";

import { ModalTabletAwareKeyboardProvider } from "../../../components/mobile/ModalTabletAwareKeyboardStickyView";

import { BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { TodoEditNewMainTimeline } from "./TodoTimeline/TodoEditTimeline";

import { TodoEditNewMainUniversal } from "./TodoEditNewMainUniversal";
import { TodoEditPopovers } from "./Popovers/TodoEditPopovers";
import { ToolbarFeature } from "widgets/src/Todo/TodoToolbar/TodoToolbarItem";
import { useTodoEditAddImage } from "./todoMutationHooks/useTodoEditAddImage";

export function TodoEditNewMain(props: TodoEditScreenProps<"Main">) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => <InnerTodoEditNewMain {...props} fetchKey={fetchKey} />}
    </LazyQueryNetworkBoundrary>
  );
}
function InnerTodoEditNewMain(
  props: TodoEditScreenProps<"Main"> & { fetchKey: string }
) {
  const data = useLazyLoadQuery<TodoEditNewMainQuery>(
    graphql`
      query TodoEditNewMainQuery($id: ID!) {
        todo(id: $id) {
          id
          title
          isOrg

          canWrite

          ...useTodoEditMoreMenuItems_todo
          ...TodoEditNewMainComponent_todo
        }
      }
    `,
    {
      id: props.route.params.id,
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  useSubscription(
    useMemo(
      () => ({
        variables: {
          todoId: props.route.params.id,
          last: 100,
        },
        subscription: graphql`
          subscription TodoEditNewMainScreenSubscription($todoId: ID!) {
            todoChangedByIds(todoIds: [$todoId]) {
              ...TodoEditNewMainComponent_todo
            }
          }
        `,
      }),
      [props.route.params.id]
    )
  );

  const [status, setStatus] = useTodoSaveStatus();

  const isReadOnly = !data.todo?.canWrite;

  const navigationHeaderOptions = useNavigationHeaderOptions();
  const navigation = props.navigation;

  const onMoveToCategory = useCallback(() => {
    if (!data.todo?.id) return;
    navigation.navigate("TodoMoveToCategory", {
      todoId: data.todo.id,
    });
  }, [navigation, data.todo?.id]);
  const ref = useRef<TodoEditNewMainComponentRef>(null);
  const menuItems = useTodoEditMoreMenuItems({
    close: navigation.goBack,
    onMoveToCategory,
    todo: data.todo ?? null,
  });

  useLayoutEffect(() => {
    navigation.setOptions({
      ...navigationHeaderOptions({
        type: "DONE",
        onDone: () => {
          navigation.goBack();
        },
        saveStatus: status,
        more: isReadOnly ? undefined : menuItems,
        title: "",
      }),
    });
  }, [menuItems, navigationHeaderOptions, navigation, status, isReadOnly]);

  const todoId = data.todo?.id!;

  const openLinkProduct = useCallback(() => {
    navigation.navigate("TodoEditLinkProduct", {
      id: todoId,
    });
  }, [navigation, todoId]);
  const openProduct = useCallback(
    (productId: string) => {
      navigation.navigate("Product", {
        screen: "ProductMain",
        params: {
          id: productId,
          type: "id",
          state: "details",
        },
      });
    },
    [navigation]
  );
  const onShowFile = useCallback(
    (fileId: string) => {
      navigation.navigate("File", {
        id: fileId,
      });
    },
    [navigation]
  );
  const openTodo = useCallback(
    (mode: "sub" | "parent", fromResponse: boolean, todoIdToOpen: string) => {
      if (mode === "sub") {
        navigation.push("TodoEdit", {
          todoId: todoIdToOpen,
          autofocusResponse: fromResponse,
        });
      } else {
        navigation.navigate("TodoEdit", {
          todoId: todoIdToOpen,
          autofocusResponse: fromResponse,
        });
        navigation.pop();
      }
    },
    [navigation]
  );
  return data.todo ? (
    <TodoEditNewMainComponent
      todo={data.todo}
      status={status}
      setStatus={setStatus}
      autoFocusResponse={props.route.params.autofocusResponse}
      openTodo={openTodo}
      onShowFile={onShowFile}
      onRequestLinkProduct={openLinkProduct}
      openProduct={openProduct}
      ref={ref}
    />
  ) : null;
}
type Feature = ToolbarFeature | "SEND_FEEDBACK";
export type TodoEditNewMainComponentRef = {
  openFeature: (feature: Feature) => void;
};
export const TodoEditNewMainComponent = memo(TodoEditNewMainComponent_);
function TodoEditNewMainComponent_(props: {
  inBottomSheet?: boolean;
  status: "saving" | "saved" | "error";
  setStatus: (key: string, status: "saving" | "saved" | "error") => void;
  todo: TodoEditNewMainComponent_todo$key;
  onShowFile: (fileId: string) => void;
  openProduct: (productId: string) => void;

  onRequestLinkProduct: () => void;
  openTodo: (
    mode: "sub" | "parent",
    fromResponse: boolean,
    todoId: string
  ) => void;

  autoFocusResponse?: boolean;
  TitleComponent?: React.ReactNode;
  highlightCamera?: boolean;
  ref?: React.RefObject<TodoEditNewMainComponentRef | null>;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoEditNewMainComponent_todo on Todo {
        ...TodoEditNewMainUniversal_todo
        ...TodoEditNewMainFooter_todo
        ...TodoEditPopovers_todo
        ...TodoEditTimeline_todo @defer
        id
        title
        canWrite
        isOrg
      }
    `,
    props.todo
  );

  const [_openFeature, setOpenFeature] = useState<Feature | null>(null);
  const dismissFeature = useCallback(() => {
    setOpenFeature(null);
  }, [setOpenFeature]);

  const openFeature = useCallback((feature: Feature) => {
    setOpenFeature(feature);
  }, []);
  useImperativeHandle(props.ref, () => ({
    openFeature,
  }));

  const addImage = useTodoEditAddImage(todo.id);

  const insets = useSafeAreaInsets();

  const contentContainerStyle = useBoxStyle({
    space: "none",
    insets: {
      bottom: props.inBottomSheet ? 0 : insets.bottom + 30,
    },
  });

  const ref = useRef<ScrollView>(null);

  const onRequestScrollToBottom = useCallback(() => {
    ref.current?.scrollToEnd({ animated: true });
  }, []);

  const InnerScrollView = useMemo(
    () =>
      props.inBottomSheet ? BottomSheetScrollView : KeyboardAwareScrollView,
    [props.inBottomSheet]
  );

  const [layout, setLayout] = useState({
    contentHeight: 0,
    layoutHeight: 0,
  });

  const maxBottomOffset = 200;
  const hasContentScroll = useMemo(() => {
    return layout.contentHeight > layout.layoutHeight + maxBottomOffset;
  }, [layout.contentHeight, layout.layoutHeight]);

  const [hasReachedBottom, setHasReachedBottom] = useState(false);

  const shouldShowScrollToBottom = useMemo(() => {
    return hasContentScroll && !hasReachedBottom;
  }, [hasContentScroll, hasReachedBottom]);
  const theme = useTheme();
  const onScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const { contentOffset, layoutMeasurement, contentSize } =
        event.nativeEvent;
      if (
        contentOffset.y + layoutMeasurement.height >=
        contentSize.height - maxBottomOffset
      ) {
        setHasReachedBottom(true);
      } else {
        setHasReachedBottom(false);
      }
    },
    [setHasReachedBottom]
  );

  return (
    <ModalTabletAwareKeyboardProvider>
      <DropFileZone
        deactivated={todo.isOrg}
        type="IMAGE"
        onDropFilesNew={async (files) => {
          files.map(async ({ file }) => {
            const { width, height, uri } = await droppedFileToImage(file);
            addImage(uri, { width, height });
          });
        }}
        maxSize={10000000}
      >
        <InnerScrollView
          style={{
            flex: 1,
            backgroundColor: theme["surface-background-light"],
          }}
          bottomOffset={100}
          scrollEventThrottle={1020}
          onScroll={hasContentScroll ? onScroll : undefined}
          contentContainerStyle={contentContainerStyle}
          keyboardShouldPersistTaps="handled"
          ref={ref}
          onLayout={(e) => {
            const height = e.nativeEvent?.layout?.height ?? 0;
            setLayout((l) => ({ ...l, layoutHeight: height }));
          }}
          onContentSizeChange={(w, h) => {
            const height = h ?? 0;
            setLayout((l) => ({ ...l, contentHeight: height }));
          }}
        >
          <TodoEditNewMainUniversal
            todo={todo}
            setStatus={props.setStatus}
            openTodo={props.openTodo}
            inBottomSheet={props.inBottomSheet ?? false}
            TitleComponent={props.TitleComponent}
            highlightCamera={props.highlightCamera ?? false}
            openFeature={openFeature}
            openProduct={props.openProduct}
            openFile={props.onShowFile}
            onRequestLinkProduct={props.onRequestLinkProduct}
          />
          <Box spaceHorizontal={"small"}>
            <Suspense
              fallback={
                <Box spaceVertical="large">
                  <AHQueryHandlerLoading />
                </Box>
              }
            >
              <TodoEditNewMainTimeline
                todo={todo}
                inBottomSheet={props.inBottomSheet ?? false}
                onRequestScrollToBottom={onRequestScrollToBottom}
              />
            </Suspense>
          </Box>
        </InnerScrollView>

        {shouldShowScrollToBottom ? (
          <ScrollToBottomButton onPress={onRequestScrollToBottom} />
        ) : null}

        <Suspense fallback={null}>
          <TodoEditNewMainFooter
            todo={todo}
            keyboardAvoiding={!props.inBottomSheet}
            autofocus={props.autoFocusResponse ?? false}
          />
        </Suspense>
      </DropFileZone>
      <Suspense fallback={null}>
        <TodoEditPopovers
          todo={todo}
          openFeature={_openFeature}
          dismissFeature={dismissFeature}
          setStatus={props.setStatus}
        />
      </Suspense>
    </ModalTabletAwareKeyboardProvider>
  );
}

export type TodoEditControllProps = {
  openProduct: (productId: string) => void;
  openFile: (fileId: string) => void;
  openFeature: (feature: ToolbarFeature | "SEND_FEEDBACK") => void;
  onRequestLinkProduct: () => void;
};
function ScrollToBottomButton(props: { onPress: () => void }) {
  const insets = useSafeAreaInsets();

  return (
    <View style={{ position: "relative", bottom: 0 }}>
      <View
        style={{
          position: "absolute",
          bottom: insets.bottom,

          alignSelf: "flex-end",
          alignItems: "center",
        }}
      >
        <TouchableBox
          onPress={props.onPress}
          spaceBottom="small"
          spaceHorizontal="large"
          hitSlop={{
            bottom: 50,
            top: 50,
            left: 10,
            right: 10,
          }}
        >
          <Circle
            size="medium"
            color="surface"
            borderColor="border-primary"
            borderWidth={1}
          >
            <AHIcon type="arrow-down" />
          </Circle>
        </TouchableBox>
      </View>
    </View>
  );
}
